import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider, useSelector } from 'react-redux';
import { RouterProvider } from 'react-router';

import { ConsentBanner } from '@/features/analytics/ConsentBanner';
import { flushRequested, outboxCleared } from '@/features/outbox/outboxSlice';
import { selectSettings } from '@/features/settings/settingsSlice';
import { AudioProvider } from '@/music/audio/AudioProvider';
import { MusicI18nProvider } from '@/music/i18n/MusicI18n';
import { GlossaryLink } from '@/pages/GlossaryLink';
import { restoreSession, useGetGlossaryQuery } from '@/shared/api/endpoints';
import { selectSessionStatus, selectUser } from '@/shared/api/sessionSlice';
import { errorReporter } from '@/shared/lib/errorReporting';
import { useAppDispatch } from '@/shared/lib/hooks';
import { hasRememberedLocale, isLocale, useContentLocale } from '@/shared/lib/locale';
import { Spinner } from '@/shared/ui/primitives';

import { ErrorBoundary } from './CrashPage';
import { createRouter } from './router';
import { connectStore, type AppStore } from './store';

const SESSION_RETRY_MS = 3_000;

const glossaryLink = (termKey: string) => <GlossaryLink termKey={termKey} />;

/**
 * Feeds the music layer what it needs from above it: the glossary of the current
 * language, the way from a term to the glossary page, and the learner's naming,
 * volume and latency preferences. The music
 * layer itself knows nothing about the API or the store.
 */
function MusicProviders({ children }: { children: ReactNode }) {
  const lang = useContentLocale();
  const settings = useSelector(selectSettings);
  const { data: glossary } = useGetGlossaryQuery(lang);
  return (
    <MusicI18nProvider glossary={glossary} naming={settings.noteNaming} termLink={glossaryLink} termMarks={settings.termHints}>
      <AudioProvider volume={settings.volume} latencyOffsetMs={settings.latencyOffsetMs}>
        {children}
      </AudioProvider>
    </MusicI18nProvider>
  );
}

/** Restores the session on startup and keeps session-dependent state in step with it. */
function SessionEffects() {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const status = useSelector(selectSessionStatus);
  const user = useSelector(selectUser);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const restore = async () => {
      const outcome = await dispatch(restoreSession())
        .unwrap()
        .catch(() => 'unreachable' as const);
      // The server could not be reached, so we still do not know: keep asking.
      if (!cancelled && outcome === 'unreachable') {
        timer = setTimeout(() => void restore(), SESSION_RETRY_MS);
      }
    };
    void restore();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dispatch]);

  useEffect(() => {
    if (status === 'authenticated') dispatch(flushRequested());
    // Unsent work belongs to the account that produced it.
    if (status === 'anonymous') dispatch(outboxCleared());
  }, [dispatch, status]);

  // A report of an error says whose session it was in: by id and role, nothing else.
  const userId = user?.id;
  const role = user?.role;
  useEffect(() => {
    errorReporter.noteUser(userId && role ? { id: userId, role } : null);
  }, [userId, role]);

  // A device with no language choice of its own follows the account.
  const accountLocale = user?.locale;
  useEffect(() => {
    if (isLocale(accountLocale) && !hasRememberedLocale()) void i18n.changeLanguage(accountLocale);
  }, [accountLocale, i18n]);

  return null;
}

function StoreConnection({ store }: { store: AppStore }) {
  useEffect(() => connectStore(store), [store]);
  return null;
}

export function App({ store }: { store: AppStore }) {
  // The pages follow from the host the application was opened at, which is known by
  // now (main.tsx) and does not change while the page lives.
  const [router] = useState(createRouter);
  return (
    <ErrorBoundary>
    <Provider store={store}>
      <StoreConnection store={store} />
      {/* Translations load asynchronously; nothing below renders without them. */}
      <Suspense fallback={<Spinner label="…" />}>
        <SessionEffects />
        <MusicProviders>
          <RouterProvider router={router} />
        </MusicProviders>
        <ConsentBanner />
      </Suspense>
    </Provider>
    </ErrorBoundary>
  );
}
